import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children,className }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [placement, setPlacement] = useState<"top" | "bottom" | "left" | "right">("top");

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show || !triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newPlacement: "top" | "bottom" | "left" | "right" = "top";
    let top = triggerRect.top - tooltipRect.height - 8;
    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

    if (top < 0) {
      // Si no cabe arriba, lo pone abajo
      newPlacement = "bottom";
      top = triggerRect.bottom + 8;
    }

    if (top + tooltipRect.height > viewportHeight) {
      // Si no cabe abajo, lo pone arriba
      newPlacement = "top";
      top = triggerRect.top - tooltipRect.height - 8;
    }

    if (left < 0) {
      // Si no cabe a la izquierda, lo pone a la derecha
      newPlacement = "right";
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + 8;
    }

    if (left + tooltipRect.width > viewportWidth) {
      // Si no cabe a la derecha, lo pone a la izquierda
      newPlacement = "left";
      left = triggerRect.left - tooltipRect.width - 8;
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    }

    setPlacement(newPlacement);
    setPosition({ top: top + window.scrollY, left: left + window.scrollX });
  }, [show]);

  return (
    <>
      {/* Elemento que activa el tooltip */}
      <div
        ref={triggerRef}
        className={className??""}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>

      {/* Tooltip renderizado en un portal para evitar problemas de posicionamiento */}
      {show &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            style={{ top: position.top, left: position.left, whiteSpace: "pre-line" }}
            className={`absolute z-50 bg-gray-700 text-white text-sm px-3 py-1 rounded shadow-lg transition-opacity duration-300 ${
              show ? "opacity-100" : "opacity-0"
            }`}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
